<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import FlashcardDeck from "@/components/FlashcardDeck.vue";
import { supabase } from "@/lib/supabase.js";

const router = useRouter();

const demoDeck = ref(null);
const enterBtnRef = ref(null);
const triggerEnterPressedEffect = () => {
  const el = enterBtnRef?.value;
  if (!el) return;
  el.classList.add("pressed-effect");
  setTimeout(() => {
    el.classList.remove("pressed-effect");
  }, 120);
};
const handleEnterKeyDown = (e) => {
  if (
    e &&
    e.key === "Enter" &&
    demoDeck &&
    demoDeck.value &&
    demoDeck.value.hasInput &&
    demoDeck.value.hasInput()
  ) {
    triggerEnterPressedEffect();
  }
};

let previousTheme = null;

const DECK_LIMIT = 3;

const demoCards = [
  {
    id: "demo-1",
    language: "es",
    language_sentence: "Yo quiero tomar café por la mañana.",
    language_word: "tomar",
    language_sentence_mappings: {
      en: "I want to drink coffee in the morning.",
    },
    language_word_mappings: { en: "to drink" },
    language_word_synonyms: ["beber"],
    // Provide your public audio URLs here:
    language_sentence_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/Spanish%20Demo%20Sentence.mp3",
    language_word_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/Spanish%20Demo%20Word(1).mp3",
    state: 1,
    part_of_speech: "verb",
    tense: "infinitive",
    person: "1st",
  },
  {
    id: "demo-2",
    language: "fr",
    language_sentence: "Elle lit un livre intéressant.",
    language_word: "livre",
    language_sentence_mappings: { en: "She is reading an interesting book." },
    language_word_mappings: { en: "book" },
    // Provide your public audio URLs here:
    language_sentence_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/French%20Demo%20Sentence.mp3",
    language_word_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/French%20Demo%20Word.mp3",
    state: 0,
    part_of_speech: "noun",
    gender: "masculine",
    number: "singular",
  },
  {
    id: "demo-3",
    language: "zh",
    language_sentence: "请问，洗手间在哪里？",
    language_word: "在",
    pinyin: "zài",
    language_sentence_mappings: { en: "Excuse me, where is the restroom?" },
    language_word_mappings: { en: "at; in; to be at" },
    // Provide your public audio URLs here:
    language_sentence_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/Mandarin%20Sentence%20Demo.mp3",
    language_word_audio:
      "https://iniknzhvvbpnfadpnwfa.supabase.co/storage/v1/object/public/spanish-sentence-audio/Mandarin%20Demo%20Word.mp3",
    state: 2,
    part_of_speech: "preposition",
  },
];

const goToLanding = () => {
  router.push("/");
};

const getStarted = () => {
  router.push({
    path: "/login",
    query: { mode: "signup", redirect: "/onboarding" },
  });
};

const leadEmail = ref("");
const leadLoading = ref(false);
const leadSuccess = ref(false);
const leadError = ref("");

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const submitLead = async () => {
  leadError.value = "";
  leadSuccess.value = false;
  const email = leadEmail.value.trim();
  if (!emailRegex.test(email)) {
    leadError.value = "Please enter a valid email.";
    return;
  }
  leadLoading.value = true;
  try {
    const { error } = await supabase.from("email_leads").insert({
      email,
      submitted_from: "white paper",
    });
    if (error) throw error;
    leadSuccess.value = true;
    leadEmail.value = "";
  } catch (e) {
    console.error("Lead submit error:", e);
    leadError.value = "Something went wrong. Please try again.";
  } finally {
    leadLoading.value = false;
  }
};

onMounted(() => {
  const root = document.documentElement;
  const prev = root.getAttribute("data-theme");
  previousTheme = prev;
  // Use light theme to match landing experience
  root.setAttribute("data-theme", "light");

  // Animate the Enter/Learn button when Enter key submits via keyboard
  window.addEventListener("keydown", handleEnterKeyDown, { passive: true });
});

onBeforeUnmount(() => {
  const root = document.documentElement;
  if (previousTheme) {
    root.setAttribute("data-theme", previousTheme);
  } else {
    root.removeAttribute("data-theme");
  }
  window.removeEventListener("keydown", handleEnterKeyDown);
});
</script>

<template>
  <div class="whitepaper">
    <!-- Same header as LandingView -->
    <header class="site-header">
      <div class="container header-inner">
        <div class="brand" @click="goToLanding">
          <img
            class="brand-logo"
            src="@/assets/Logo Expanded.svg"
            alt="Lexio logo"
          />
        </div>
        <nav class="nav-cta">
          <router-link class="btn btn-ghost" to="/login">Log in</router-link>
          <button class="btn btn-accent" @click="getStarted">
            Get started
          </button>
        </nav>
      </div>
    </header>

    <!-- Back arrow to landing -->
    <div class="back-bar">
      <div class="container">
        <router-link class="back-link" to="/" aria-label="Back to landing">
          <img
            class="back-icon"
            src="@/assets/LeftArrow.svg"
            alt="Back arrow"
          />
          <span>Back to landing</span>
        </router-link>
      </div>
    </div>

    <!-- White paper content -->
    <main class="content">
      <article class="container article">
        <header class="article-header">
          <h1 class="title">Lexical White Paper</h1>
          <div class="meta">
            <span>Published</span>
            <time :datetime="new Date().toISOString()"> 11/1/2025 </time>
          </div>
        </header>

        <section class="section">
          <p>
            I've learned three languages in three years. Spanish, French, and
            Italian. I think the mainstream pedagogy for language learning is
            flawed; I think it’s the reason most people who attempt to learn a
            second language fail. In this paper I’ll explain what I’ve learned
            about language acquisition from my experiences learning Spanish,
            French, and Italian, and how I've implemented that understanding
            into my app, Lexical, which I’m now using to teach myself Mandarin.
          </p>
          <br />
        </section>

        <section class="section">
          <h2>
            Why you should be suspicious of traditional language-learning
            pedagogy
          </h2>
          <p>
            Anyone who has made a semi-serious attempt to acquire a second
            language, myself included, has received the deeply unhelpful advice
            <em>The best way to learn a language is by immersion!</em> Immersion
            is, in fact, the most effective, but it’s a rare and often costly
            experience—frequently regarded as a kind of mysterious and
            unreplicable black box.
          </p>
          <p>
            The strange thing about most classrooms, and language apps, is how
            profoundly they diverge from immersion. Traditional instruction
            fixates on grammar, spelling, pronunciation, and cultural
            heuristics, while immersion offers no formal training in any of
            these areas—yet it’s widely regarded as far more effective. Seen
            from this contrast, the typical classroom approach transforms an
            innate human capacity—the ability to acquire language by
            osmosis—into an over-engineered academic exercise.
          </p>

          <p>
            Do we know what makes immersion so much more effective than the
            contrived language instruction it’s compared to, and can those key
            qualities be replicated? My answer is yes.
          </p>

          <p>
            Immersion is not a black box. The mainstream pedagogy is bad because
            it’s not centered on the key qualities of immersion. Here are those
            core mechanisms, and how they can be reproduced.
          </p>
          <br />
        </section>

        <section class="section">
          <h2>The primacy of inference</h2>
          <p>
            In pure immersion, words, phrases, grammatical structures, and
            pronunciation are all learned nearly entirely by inference. At first
            this happens only observationally, as the meanings of words are
            derived from contextual associations with objects and events. Over
            time, however, meaning is increasingly inferred lexically, or
            directly from the relationships between words.
          </p>
          <p>
            Once a word’s flexible yet consistent meaning has been acquired, it
            provides the contextual scaffolding needed to deduce the meanings of
            less common or more nuanced words. This creates a self-reinforcing
            engine of inference—the more you’ve inferred, the more you can
            infer. It might take fifty observations to infer the meaning of the
            first word spontaneously, but eventually you can infer new words
            lexically—often from a single exposure, in real time.
          </p>
          <p>
            When I learned Spanish, mostly by immersion, for the first several
            months it felt like I wasn't learning anything at all, and then
            suddenly, within a 1–2 week span, I felt confident listening and
            speaking. It is the compounding nature of inference that makes
            starting to learn a new language so difficult, but so rewarding for
            the persistent. Notably, however, it took me only a few months to
            learn Spanish—which is fast, even for immersion. What gives?
          </p>

          <p>
            I was able to learn so quickly because I already had some basic
            exposure to Spanish grammar and vocabulary. Inference requires
            having a blend of known and unknown material, and thus it can be
            dramatically accelerated by leveraging the language(s) you already
            know to learn about the language you are trying to acquire. These
            conscious heuristics can jump-start the inference engine, allowing
            sooner pattern recognition, but will ultimately give way to their
            more nuanced and implicit formulations and be forgotten
            <sup id="cite-1" class="citation"><a href="#ref-1">1</a></sup
            >. Any heuristic learned, whose analogue you don’t use in your
            native language, is a means to an end, not to be overemphasized.
          </p>
          <p>
            Fluency is an unconscious competence, that is achieved by inference
            <em>en masse</em>. You can no more speak a language via grammar
            rules than you can catch a ball by pausing to calculate its
            trajectory. In the words of my favorite band: “there’s no algorithm
            for intuition.”<sup id="cite-2" class="citation"
              ><a href="#ref-2">2</a></sup
            >
            The question isn’t whether or not to rely on inference, but rather
            how to accelerate the process.
          </p>
          <p>
            The goal is not to go to France and know every word of every French
            sentence. The goal is for every French sentence to contain words you
            know. A mix of known and unknown. You want to get to this point as
            fast as possible and this requires not only inference but also
            memorization<sup id="cite-3" class="citation"
              ><a href="#ref-3">3</a></sup
            >. This takes us to our second core mechanism of immersion: spaced
            repetition.
          </p>
          <br />
        </section>

        <section class="section">
          <h2>Spaced repetition</h2>
          <p>
            In immersion the constant exposure to the language not only produces
            continuous opportunities for inferential learning, it also functions
            as a natural
            <a href="https://en.wikipedia.org/wiki/Spaced_repetition"
              >spaced repetition</a
            >
            that makes you remember what you learn.
          </p>
          <p>
            For languages across the board, the 5,000 most common words in the
            language make up 90–95% of all speech.<sup
              id="cite-4"
              class="citation"
              ><a href="#ref-4">4</a></sup
            >
            <sup id="cite-5" class="citation"><a href="#ref-5">5</a></sup>
            <sup id="cite-6" class="citation"><a href="#ref-6">6</a></sup>
            <sup id="cite-7" class="citation"><a href="#ref-7">7</a></sup>
            <sup id="cite-8" class="citation"><a href="#ref-8">8</a></sup>
            Having that suite of 5,000 core words in long-term, quick-access
            memory is a necessary condition for fluency.
          </p>
          <p>
            Outside of immersion, memorizing 5,000 words is doable but certainly
            non-trivial—it demands a disciplined, systematic approach that’s
            rarely considered in classrooms. Additionally, immersion delivers
            the words and phrases of a language weighted by frequency of use.
            This is definitionally obvious, however, it’s an important nuance
            that is easy to overlook. Immersion’s spaced repetition, weighted by
            usage, maximizes the inferential value of future exposures to the
            language—accelerating arrival to the point at which every sentence
            includes some known vocabulary.
          </p>
          <br />
        </section>

        <section class="section">
          <h2>Speed and consistency</h2>
          <p>
            Last but not least, learning a language by immersion works because
            it forces you to learn fast.
          </p>
          <p>
            Language learning is driven by compounding mechanisms that can work
            for you—or against you. Like launching a rocket into orbit,
            significant and sustained effort is required to reach escape
            velocity, but once in space, maintaining orbit requires a
            comparatively small amount of energy. Attaining fluency is much the
            same. When you have turned your conscious competence into an
            unconscious competence the language has crystalized and becomes easy
            to maintain.
          </p>
          <p>
            Conversely, taking too long to achieve fluency is like taking too
            long to reach orbit—it becomes prohibitively costly. Taking too long
            to learn is like adding more weight to your payload: you have to add
            more fuel not only for the weight of the additional payload but also
            for the weight of the additional fuel. If you quit before you reach
            orbit your progress decays rapidly. The more you know, the more you
            can forget, and the more time you must spend maintaining your
            knowledge. Your friend who has been grinding Duolingo for three
            years and still doesn’t speak Spanish is the perfect example of
            this: they are forgetting at the same rate they are learning.<sup
              id="cite-9"
              class="citation"
              ><a href="#ref-9">9</a></sup
            >
          </p>
          <br />
        </section>

        <section class="section">
          <h2>How Lexical emulates immersion</h2>
          <p>
            Lexical emulates immersion by focusing squarely on the
            <em>primary</em>
            necessary condition for inference and fluency: having the 5,000 most
            common words of the language stored in long-term memory.
          </p>
          <p>
            Lexical militantly focuses on the 5,000 words. It teaches you these
            words as efficiently as possible, with context-rich audio vocab
            flashcards, powered by a spaced repetition algorithm. I promise that
            Lexical’s flashcards are not more boring than immersion is
            uncomfortable. Each of the 5,000 most common words has a flash card.
            Each flash card has both the word standing alone and the word
            situated in a common sentence giving the context necessary for
            inferential learning. The sentence and word translations are given,
            your task is to recall the word in the language you are trying to
            learn. Here is a brief demo:
          </p>
          <div class="demo-block">
            <FlashcardDeck
              ref="demoDeck"
              :preloaded-cards="demoCards"
              :auto-fetch="false"
              :limit="DECK_LIMIT"
            />
          </div>
          <div class="demo-bottom-bar">
            <div class="demo-bottom-bar-inner">
              <button
                type="button"
                class="btn btn-accent enter-btn"
                ref="enterBtnRef"
                @click="
                  demoDeck && demoDeck.checkAnswer && demoDeck.checkAnswer()
                "
                :aria-label="
                  demoDeck && demoDeck.hasInput && demoDeck.hasInput()
                    ? 'Enter word'
                    : 'Learn Word'
                "
                :title="
                  demoDeck && demoDeck.hasInput && demoDeck.hasInput()
                    ? 'Enter word'
                    : 'Learn Word'
                "
              >
                {{
                  demoDeck && demoDeck.hasInput && demoDeck.hasInput()
                    ? "Enter word"
                    : "Learn Word"
                }}
                <img
                  class="keyboard-return-icon desktop-only"
                  src="@/assets/KeyboardReturnIcon.svg"
                  alt=""
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
          <p>
            The spaced repetition algorithm optimizes when each card appears,
            ensuring that every word is efficiently learned
            <em>and retained</em> over time. It works by showing you a word
            repeatedly until its meaning is remembered, then resurfacing the
            word at expanding intervals to prevent you from forgetting it. Like
            memory itself, this process works best when Lexical is used
            consistently; with 20–30 minutes of review a day. This system makes
            it possible to learn and retain all 5,000 words in under a year—and
            in some cases, in as little as six to eight months.
          </p>
          <p>
            Emulating immersion, flashcards are presented in order of word
            frequency, maximizing the inferential value of future language
            exposures. Even within the first few cards, previously learned words
            resurface in the example sentences for new vocabulary, naturally
            reinforcing your memory of those words and enabling inferential
            learning.
          </p>
          <p>
            Another key element of immersion is, of course, the auditory
            exposure. Lexical always plays a recording of the word, and a
            recording of the contextual sentence. I believe that poor
            pronunciation is primarily the result of visual,
            <em>silent</em> exposure to foreign words. Seeing a word without
            hearing it gives the learner the opportunity to practice
            mispronouncing the word in their head. <em>Hearing</em> words, from
            the very beginning, dramatically improves long-term pronunciation
            outcomes, and makes the transition to conversation orders of
            magnitude easier.
          </p>
          <p>
            Conceptually, Lexical isn’t novel<sup id="cite-10" class="citation"
              ><a href="#ref-10">10</a></sup
            >. The technology behind Lexical can now be realized for cheaper,
            because of the advent of large language models. At their core, LLMs
            are exceptionally good language emulators. I’ll be the first to
            assert that LLMs are not a flawless silver-bullet, but they are
            definitionally good at making statistically common, grammatical
            sentences. They make creating high-quality language-learning
            materials dramatically easier and cheaper. For Lexical, this
            includes the generation of the context sentences, and the
            text-to-speech audio—which is now orders of magnitude more
            human-sounding than the audio that was possible a few years ago.
          </p>
          <br />
        </section>

        <section class="section">
          <h2>My process</h2>
          <p>
            My study routine, the one that I have used and will continue to use
            forever, is to grind on Lexical for 15–30 minutes a day until I’ve
            memorized the 5,000 words. I also do an initial stint of grammar
            study, to kick-start pattern recognition. I recommend
            <a href="https://language-blueprints.com/">these grammar guides</a>.
            Those are likely sufficient, but if you want a more thorough study,
            I’ve enjoyed the McGraw Hill textbooks.
          </p>
          <p>
            Diversified exposure to the language is crucial. Podcasts are
            convenient. I also recommend movies and shows with the audio
            <em> and subtitles</em> set to the language you are learning. Voice
            conversations with GPT-5, now almost entirely free, is a great way
            to practice speaking and listening. Once you’ve learned a few
            thousand words, going to the country that speaks your target
            language, even just for a week or two, will of course significantly
            accelerate your language learning. Ideally, you make a friend there
            so you can continue speaking to a native. In general, I think
            materials marketed to language learners are overrated. Once you’ve
            learned even a few hundred words, I believe it’s far more effective
            to start consuming real media in your target language.
          </p>
          <p>
            In summary, Lexical combined with 2–3 of these elements is
            sufficient to get you to fluency:
          </p>
          <ul>
            <li>GPT-5 Voice Mode</li>
            <li>movies / TV shows</li>
            <li>podcasts</li>
            <li>a 2 week – 2 month stint in the target country</li>
          </ul>
          <br />
        </section>

        <section class="section">
          <h3>If you’ve made it this far</h3>
          <p>
            Thanks for reading! I love chatting about this stuff, so feel free
            to reach out to me at taylorboan@me.com. I want to continue building
            out the features and capabilities of Lexical by including some of
            those supplemental experiences right in the app. If you want to keep
            up with the project, sign up to receive spam-free updates below. If
            you want to learn a new language and support the project, sign up
            for Lexical. It’s only $2.99 a month and the first 150 cards are
            free. Or, if you just want to keep up with the project, sign up to
            receive spam-free updates below. I really believe that you can learn
            a language for the cost of a cup of coffee.
            <br />
            <br />
          </p>
        </section>

        <section class="section subscribe">
          <form
            class="subscribe-form"
            @submit.prevent="submitLead"
            aria-label="Subscribe for updates"
          >
            <input
              class="subscribe-input"
              type="email"
              v-model="leadEmail"
              placeholder="johnnyappleseed@me.com"
              required
              aria-label="Email address"
            />
            <button
              class="subscribe-button"
              type="submit"
              :disabled="leadLoading"
            >
              {{ leadLoading ? "Submitting..." : "Subscribe" }}
            </button>
          </form>
          <p v-if="leadSuccess" class="subscribe-success">
            Thanks! You're on the list.
          </p>
          <p v-if="leadError" class="subscribe-error">{{ leadError }}</p>
          <br />
        </section>

        <section class="section references" id="references">
          <h2>References</h2>
          <ol>
            <li id="ref-1">
              <a
                href="https://news.ycombinator.com/item?id=44907531"
                target="_blank"
                rel="noopener"
                >I've personally experienced this, but here is another
                example.</a
              >
              <a
                class="backref"
                href="#cite-1"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-2">
              <a
                href="https://genius.com/Coin-learning-lyrics"
                target="_blank"
                rel="noopener"
                >This whole album is fire.</a
              >
              <a
                class="backref"
                href="#cite-2"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-3">
              <a
                href="https://news.ycombinator.com/item?id=32982513"
                target="_blank"
                rel="noopener"
                >Learning is memorization.</a
              >
              <a
                class="backref"
                href="#cite-3"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-4">
              <a
                href="https://files.eric.ed.gov/fulltext/EJ887873.pdf"
                target="_blank"
                rel="noopener"
                >Lexical Threshold - Inspired the name of the app.</a
              >
              <a
                class="backref"
                href="#cite-4"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-5">
              <a
                href="https://www.researchgate.net/publication/228633252_Readability_of_French_as_a_Foreign_Language_and_its_Uses"
                target="_blank"
                rel="noopener"
                >5,000 words is a threshold for spontaneous inference.</a
              >
              <a
                class="backref"
                href="#cite-5"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-6">
              <a
                href="https://www.lextutor.ca/cover/papers/nation_2006.pdf"
                target="_blank"
                rel="noopener"
                >More words are required for written text.</a
              >
              <a
                class="backref"
                href="#cite-6"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-7">
              <a
                href="https://www2.hawaii.edu/~readfl/rfl/April2005/chujo/chujo.html"
                target="_blank"
                rel="noopener"
                >This one provides a nice graph of text coverage vs. word
                count.</a
              >
              <a
                class="backref"
                href="#cite-7"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-8">
              <a
                href="https://news.ycombinator.com/item?id=20721736"
                target="_blank"
                rel="noopener"
                >Some languages require fewer words. For Kanji its only ~ 777
                words!</a
              >
              <a
                class="backref"
                href="#cite-8"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-9">
              <a
                href="https://news.ycombinator.com/item?id=45425061"
                target="_blank"
                rel="noopener"
                >Duolingo sucks, at least for learning a language.</a
              >
              <a
                class="backref"
                href="#cite-9"
                aria-label="Back to citation"
              ></a>
            </li>
            <li id="ref-10">
              <a
                href="https://news.ycombinator.com/item?id=45359524"
                target="_blank"
                rel="noopener"
                >Other apps exist, including Anki.</a
              >
              <a
                class="backref"
                href="#cite-10"
                aria-label="Back to citation"
              ></a>
            </li>
          </ol>
        </section>

        <footer class="article-footer">
          <router-link class="btn btn-ghost" to="/">Back to home</router-link>
          <button class="btn btn-accent" @click="getStarted">
            Get started
          </button>
        </footer>
      </article>
    </main>
  </div>
</template>

<style scoped>
/* Layout primitives */
.container {
  width: min(1100px, calc(100% - 32px));
  margin-inline: auto;
}

/* Header (same styles as LandingView) */
.site-header {
  position: sticky;
  top: 0;
  backdrop-filter: saturate(180%) blur(8px);
  background: color-mix(in oklab, var(--app-bg) 86%, transparent);
  border-bottom: 1px solid
    color-mix(in oklab, var(--text-color) 8%, transparent);
  z-index: 10;
}
.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}
.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.brand-logo {
  height: 22.4px;
  width: auto;
}
.brand-logo.small {
  height: 17.6px;
  width: auto;
}
.brand-name {
  font-weight: 700;
  letter-spacing: 0.2px;
  color: var(--text-color);
}
.nav-cta {
  display: flex;
  gap: 8px;
}
.site-header .nav-cta .btn {
  font-size: 1rem;
  font-weight: 700;
  border-radius: 12px;
}
@media (max-width: 800px) {
  .nav-cta {
    gap: 6px;
  }
  .site-header .nav-cta .btn {
    --btn-pad: 8px 10px;
    flex: 0 0 auto;
    width: auto;
    white-space: nowrap;
  }
}

/* Back bar */
.back-bar {
  border-bottom: 1px solid
    color-mix(in oklab, var(--text-color) 8%, transparent);
  background: color-mix(in oklab, var(--app-bg) 95%, transparent);
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  color: inherit;
  text-decoration: none;
  font-weight: 600;
}
.back-icon {
  width: 16px;
  height: 16px;
  filter: invert(0);
}

/* Buttons (same tokens as LandingView) */
.btn {
  --btn-bg: var(--hover-bg);
  --btn-fg: var(--text-color);
  --btn-br: 10px;
  --btn-pad: 10px 14px;

  background: var(--btn-bg);
  color: var(--btn-fg);
  border: none;
  border-radius: var(--btn-br);
  padding: var(--btn-pad);
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.05s ease, background 0.2s ease, opacity 0.2s ease;
}
.btn:hover {
  transform: translateY(-1px);
}
.btn:active {
  transform: translateY(0);
}
.btn-ghost {
  background: transparent;
  border: 1px solid color-mix(in oklab, var(--text-color) 12%, transparent);
}
.btn-accent {
  --btn-bg: var(--accent);
  --btn-fg: #fff;
}
.btn-lg {
  --btn-pad: 12px 18px;
  border-radius: 12px;
  font-size: 1rem;
}

/* Article/content */
.content {
  padding: clamp(20px, 4vw, 36px) 0 56px;
  background: var(--app-bg);
}
.article {
  max-width: 880px;
}
.article-header {
  margin: 8px 0 18px;
}
.title {
  font-size: clamp(1.8rem, 1.3rem + 2vw, 2.4rem);
  line-height: 1.15;
  margin: 0 0 6px;
  letter-spacing: -0.02em;
  color: var(--text-color);
}
.subtitle {
  margin: 0 0 8px;
  font-size: 1.05rem;
  color: color-mix(in oklab, var(--text-color) 76%, transparent);
}
.meta {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 0.9rem;
  color: color-mix(in oklab, var(--text-color) 68%, transparent);
}
.section {
  margin: 18px 0;
}
.section h2 {
  margin: 0 0 8px;
  font-size: 1.4rem;
}
.section h3 {
  margin: 12px 0 6px;
  font-size: 1.05rem;
}
.section p,
.section li {
  color: color-mix(in oklab, var(--text-color) 86%, transparent);
  line-height: 1.6;
}
.section ul {
  padding-left: 18px;
}
.article-footer {
  display: flex;
  gap: 8px;
  margin-top: 20px;
}
.article-footer .btn {
  font-size: 1rem;
}

/* Responsive tweaks */
@media (max-width: 760px) {
  .article-footer {
    flex-wrap: wrap;
  }
}
/* Demo controls */
.demo-block {
  margin: 12px 0 16px;
}

.demo-bottom-bar {
  width: 650px;
  max-width: 100%;
  margin: 6px auto 14px;
  margin-top: 0px;
}
.demo-bottom-bar-inner {
  background: var(--hover-bg);
  color: var(--text-color);
  border-radius: 13px;
  padding: 6px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.enter-btn {
  font-weight: 400;
  white-space: nowrap;
  transition: transform 0.06s ease, background 0.15s ease, box-shadow 0.15s ease,
    filter 0.15s ease;
}
.enter-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px color-mix(in oklab, var(--btn-bg) 40%, transparent);
  filter: saturate(1.05);
}
.enter-btn:active:not(:disabled),
.enter-btn.pressed-effect {
  transform: translateY(0);
  background: color-mix(in oklab, var(--btn-bg) 90%, black 10%);
  filter: saturate(0.98);
}
.enter-btn .keyboard-return-icon {
  width: 18px;
  height: 18px;
  margin-left: 8px;
}
.desktop-only {
  display: none;
}
@media (min-width: 769px) {
  .desktop-only {
    display: inline-block;
  }
}

.demo-block :deep(.flashcard-container) {
  margin-top: 8px;
  padding-bottom: 10px;
}

/* Subscribe form (Substack-style) */
.subscribe {
  margin: 18px 0 10px;
  text-align: center;
}
.subscribe-form {
  display: flex;
  align-items: stretch;
  gap: 0;
  background: color-mix(in oklab, var(--app-bg) 80%, #0b0b0b);
  border: 1.5px solid var(--accent);
  border-radius: 8px;
  overflow: hidden;
  max-width: 350px;
  margin: 0 auto;
  max-height: 40px;
}
.subscribe-input {
  flex: 1 1 auto;
  background: color-mix(in oklab, var(--app-bg) 90%, #0b0b0b);
  color: var(--text-color);
  border: none;
  padding: 12px 14px;
  outline: none;
  min-width: 50px;
  font-size: 1rem;
  /*font-weight: 700;*/
}

.subscribe-input::placeholder {
  color: color-mix(in oklab, var(--text-color) 30%, var(--app-bg));
}

.subscribe-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: #fff;
  border: none;
  padding: 12px 16px;
  font-size: 1rem;
  font-weight: 1;
  cursor: pointer;
  white-space: nowrap;
  min-width: 115px;
  letter-spacing: 0.05rem;
}
.subscribe-button:disabled {
  opacity: 0.7;
  cursor: default;
}
.subscribe-success {
  color: #16a34a;
  margin-top: 8px;
  text-align: center;
}
.subscribe-error {
  color: #ef4444;
  margin-top: 8px;
  text-align: center;
}
</style>
